import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Briefcase, Heart, ArrowRight, Shield, FileText, 
  ClipboardList, Users, Phone, Mail, CheckCircle
} from 'lucide-react';

const AVIDA_LOGO = "https://customer-assets.emergentagent.com/job_packet-digitizer/artifacts/f0t7kezs_Avida%20Logo%20Full%20Color.png";
const AVIDA_LOGO_WHITE = "https://customer-assets.emergentagent.com/job_efcc7f4c-7598-4a68-9a23-df11959ff0cc/artifacts/nidh4cth_Avida%20Logo%20White..png";

export default function LandingPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Premium Header */}
      <header className="header-glass sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <img 
            src={AVIDA_LOGO} 
            alt="Avida Personal Care" 
            className="h-12 md:h-14"
            data-testid="avida-logo"
          />
          <Button 
            variant="outline"
            onClick={() => navigate('/login')}
            className="btn-outline-navy h-11 px-6"
            data-testid="staff-login-btn"
          >
            Staff Login
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-blue-50/30" />
        
        {/* Large Avida Logo Watermark - Yellow - Centered */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <img 
            src={AVIDA_LOGO} 
            alt="" 
            className="w-[900px] md:w-[1100px] lg:w-[1400px] opacity-[0.15]"
            style={{ 
              filter: 'brightness(0) saturate(100%) invert(85%) sepia(90%) saturate(2000%) hue-rotate(360deg) brightness(110%) contrast(105%)',
            }}
          />
        </div>
        
        {/* Decorative elements */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-gradient-to-br from-[#FBD704]/10 to-transparent rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-[#0A368F]/5 to-transparent rounded-full blur-3xl" />
        
        <div className="relative max-w-7xl mx-auto px-6 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="text-center max-w-4xl mx-auto animate-fade-up">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 badge-gold mb-8">
              <CheckCircle className="w-4 h-4" />
              <span>Wisconsin Licensed Personal Care Provider</span>
            </div>
            
            {/* Main headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#0A368F] mb-6">
              Avida Forms Portal
            </h1>
            
            {/* Subheadline */}
            <p className="text-lg md:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Complete forms online for employment applications or client services. 
              Secure, efficient, and designed for care.
            </p>
          </div>
        </div>
      </section>

      {/* Pathway Cards Section */}
      <section className="relative py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 animate-stagger">
            
            {/* Employment Forms Card */}
            <div 
              className="pathway-card group cursor-pointer"
              onClick={() => navigate('/employment')}
              data-testid="employment-pathway-card"
            >
              <div className="relative p-8 md:p-10 bg-gradient-to-br from-[#0A368F] via-[#1E50A0] to-[#3D97FF] rounded-3xl overflow-hidden">
                {/* Decorative circles */}
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/5 rounded-full" />
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/5 rounded-full" />
                
                <div className="relative">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                    <Briefcase className="w-8 h-8 text-white" />
                  </div>
                  
                  {/* Content */}
                  <div className="mb-8">
                    <span className="text-[#FBD704] text-sm font-semibold uppercase tracking-wider">
                      For Job Applicants
                    </span>
                    <h2 className="text-2xl md:text-3xl font-medium text-white mt-2 mb-3">
                      Employment Forms
                    </h2>
                    <p className="text-blue-100 leading-relaxed">
                      Apply for a position at Avida Personal Care. Complete your application and required background checks online.
                    </p>
                  </div>
                  
                  {/* Features list */}
                  <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-3 text-white/90">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                        <FileText className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-sm">Employment Application</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/90">
                      <div className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center">
                        <Shield className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-sm">Background Information Disclosure</span>
                    </div>
                    <div className="flex items-center gap-3 text-white/50">
                      <div className="w-6 h-6 rounded-full bg-white/5 flex items-center justify-center">
                        <ClipboardList className="w-3.5 h-3.5" />
                      </div>
                      <span className="text-sm">W-9 / W-4 Forms (Coming Soon)</span>
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <Button 
                    className="w-full btn-gold h-14 text-base font-semibold group-hover:shadow-2xl transition-shadow"
                    data-testid="start-employment-btn"
                  >
                    Start Application
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Client & Patient Forms Card */}
            <div 
              className="pathway-card group cursor-pointer"
              onClick={() => navigate('/login')}
              data-testid="client-pathway-card"
            >
              <div className="relative p-8 md:p-10 bg-white rounded-3xl border-2 border-slate-100 overflow-hidden group-hover:border-[#0A368F]/20 transition-colors">
                {/* Decorative elements */}
                <div className="absolute -top-20 -right-20 w-60 h-60 bg-gradient-to-br from-[#FBD704]/10 to-transparent rounded-full" />
                <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-gradient-to-tr from-[#0A368F]/5 to-transparent rounded-full" />
                
                <div className="relative">
                  {/* Icon */}
                  <div className="w-16 h-16 bg-gradient-to-br from-[#FBD704] to-[#FCE44A] rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-amber-200/50">
                    <Heart className="w-8 h-8 text-[#0A368F]" />
                  </div>
                  
                  {/* Content */}
                  <div className="mb-8">
                    <span className="text-[#0A368F] text-sm font-semibold uppercase tracking-wider">
                      For Avida Staff
                    </span>
                    <h2 className="text-2xl md:text-3xl font-medium text-[#0B162C] mt-2 mb-3">
                      Client & Patient Forms
                    </h2>
                    <p className="text-slate-600 leading-relaxed">
                      Complete client paperwork digitally during in-home visits. Auto-fill demographics and capture signatures.
                    </p>
                  </div>
                  
                  {/* Features list */}
                  <div className="space-y-3 mb-8">
                    <div className="flex items-center gap-3 text-slate-700">
                      <div className="w-6 h-6 rounded-full bg-[#0A368F]/10 flex items-center justify-center">
                        <FileText className="w-3.5 h-3.5 text-[#0A368F]" />
                      </div>
                      <span className="text-sm">Medical Release Authorization</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <div className="w-6 h-6 rounded-full bg-[#0A368F]/10 flex items-center justify-center">
                        <ClipboardList className="w-3.5 h-3.5 text-[#0A368F]" />
                      </div>
                      <span className="text-sm">Screening Packet (6 Forms)</span>
                    </div>
                    <div className="flex items-center gap-3 text-slate-700">
                      <div className="w-6 h-6 rounded-full bg-[#0A368F]/10 flex items-center justify-center">
                        <Users className="w-3.5 h-3.5 text-[#0A368F]" />
                      </div>
                      <span className="text-sm">PCST Authorization & Transfer</span>
                    </div>
                  </div>
                  
                  {/* CTA Button */}
                  <Button 
                    className="w-full btn-navy h-14 text-base font-semibold"
                    data-testid="staff-login-card-btn"
                  >
                    Staff Login Required
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-16 bg-gradient-to-b from-white to-slate-50/50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="space-y-2">
              <div className="text-3xl font-light text-[#0A368F]">100+</div>
              <div className="text-sm text-slate-500">Care Professionals</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-light text-[#0A368F]">500+</div>
              <div className="text-sm text-slate-500">Clients Served</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-light text-[#0A368F]">HIPAA</div>
              <div className="text-sm text-slate-500">Compliant</div>
            </div>
            <div className="space-y-2">
              <div className="text-3xl font-light text-[#0A368F]">24/7</div>
              <div className="text-sm text-slate-500">Support Available</div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A368F] text-white py-10">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            {/* Logo on Left */}
            <div className="flex-shrink-0">
              <img 
                src={AVIDA_LOGO_WHITE} 
                alt="Avida Personal Care" 
                className="w-36 md:w-44"
              />
            </div>
            
            {/* Center - Tagline & Address */}
            <div className="text-center flex-1">
              <p className="text-blue-200 text-sm mb-2">
                Compassionate care in the comfort of home
              </p>
              <p className="text-blue-100 text-sm">
                2500 West Layton Avenue, Milwaukee, WI 53221
              </p>
            </div>
            
            {/* Right - Contact info */}
            <div className="flex flex-col items-center md:items-end gap-2 text-sm">
              <div className="flex items-center gap-2 text-blue-200">
                <Phone className="w-4 h-4" />
                <span>(414) 348-4600</span>
              </div>
              <div className="flex items-center gap-2 text-blue-200">
                <Mail className="w-4 h-4" />
                <span>info@avidahealth.org</span>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/10 mt-8 pt-6 text-center">
            <p className="text-blue-300/60 text-xs">
              © {new Date().getFullYear()} Avida Personal Care. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
